/**
 * マスタの読み書き。Firestoreへのアクセスはこのファイルに閉じる。
 * 画面は必ずここ経由でマスタを触ること(クエリの散逸を防ぐ)。
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getDocsFromServer,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type WriteBatch,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { DEFAULT_PAYER_SELECT_LABELS, departmentManagerIds } from '@core/lib/types';
import type { RecurringSettings } from '@core/lib/types';
import type {
  FlowRole,
  PayerLabels,
  Permissions,
  Account,
  ApprovalRule,
  Department,
  FiscalSettings,
  PlVisibilitySettings,
  SubAccount,
  TaxSettings,
  User,
} from '@core/lib/types';

function withId<T>(snap: { id: string; data: () => unknown }): T {
  return { id: snap.id, ...(snap.data() as object) } as T;
}

// --- 事業部 -------------------------------------------------------------------

export async function fetchDepartments(): Promise<Department[]> {
  const snap = await getDocs(query(collection(getDb(), 'departments'), orderBy('display_order')));
  return snap.docs.map((d) => withId<Department>(d));
}

export async function updateDepartment(
  id: string,
  patch: Partial<Pick<Department, 'name' | 'short_name' | 'display_order' | 'active'>>,
): Promise<void> {
  await updateDoc(doc(getDb(), 'departments', id), { ...patch, updated_at: serverTimestamp() });
}

/**
 * 事業部を追加する(要件定義書 2「事業部は増減しうる」)。
 * 追加した事業部は汎用事業部モジュールで即座に全社PLへ参加する。
 * 固有ロジックが必要になった時点で `/modules/<事業部名>/` を作り、moduleRegistry に登録する。
 */
export async function createDepartment(input: {
  id: string;
  name: string;
  short_name: string;
  display_order: number;
}): Promise<void> {
  const id = input.id.trim().toLowerCase();
  if (!/^[a-z0-9-]+$/.test(id)) {
    throw new Error('事業部IDは英小文字・数字・ハイフンで入力してください(例: shinjuku)');
  }
  if (!input.name.trim()) throw new Error('事業部名を入力してください');

  const ref = doc(getDb(), 'departments', id);
  if ((await getDoc(ref)).exists()) throw new Error(`事業部ID「${id}」は既に使われています`);

  await setDoc(ref, {
    name: input.name.trim(),
    short_name: (input.short_name || input.name).trim(),
    display_order: input.display_order,
    module_key: id,
    is_headquarters: false,
    active: true,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
}

// --- 勘定科目 ---------------------------------------------------------------

export async function fetchAccounts(): Promise<Account[]> {
  const snap = await getDocs(query(collection(getDb(), 'accounts'), orderBy('display_order')));
  return snap.docs.map((d) => withId<Account>(d));
}

export async function fetchSubAccounts(): Promise<SubAccount[]> {
  const snap = await getDocs(query(collection(getDb(), 'sub_accounts'), orderBy('display_order')));
  return snap.docs.map((d) => withId<SubAccount>(d));
}

export async function fetchSubAccountsOf(accountId: string): Promise<SubAccount[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), 'sub_accounts'),
      where('account_id', '==', accountId),
      orderBy('display_order'),
    ),
  );
  return snap.docs.map((d) => withId<SubAccount>(d));
}

export async function upsertAccount(account: Account): Promise<void> {
  const { id, ...rest } = account;
  await setDoc(
    doc(getDb(), 'accounts', id),
    {
      ...rest,
      // 旧データ編集時に undefined を書かないよう正規化(2026-08-26 追加フィールド)
      item_category: rest.item_category ?? null,
      abbr: rest.abbr ?? null,
      description: rest.description ?? null,
      department_ids: rest.department_ids ?? null,
    },
    { merge: true },
  );
}

export async function upsertSubAccount(subAccount: SubAccount): Promise<void> {
  const { id, ...rest } = subAccount;
  await setDoc(doc(getDb(), 'sub_accounts', id), rest, { merge: true });
}

// --- ユーザー ---------------------------------------------------------------

export async function fetchUsers(): Promise<User[]> {
  const snap = await getDocs(query(collection(getDb(), 'users'), orderBy('email')));
  return snap.docs.map((d) => withId<User>(d));
}

/**
 * 名簿を必ずサーバーから取る(2026-09-30 不具合対応)。
 *
 * 通常の取得は、通信が切れていると端末内のキャッシュで代用される。
 * キャッシュには一部の人しか入っていないことがあり、それで
 * 「この事業部に部門長がいない」と誤判定すると**部門長確認を飛ばして**申請が進んでしまう
 * (2026-09-30 の実測で再現)。承認ルートと通知の宛先を決めるときは、
 * 古い情報で判断せず、つながらなければエラーにする
 */
export async function fetchUsersFromServer(): Promise<User[]> {
  const snap = await getDocsFromServer(query(collection(getDb(), 'users'), orderBy('email')));
  return snap.docs.map((d) => withId<User>(d));
}

export type UserProfileInput = Pick<
  User,
  | 'email'
  | 'contact_email'
  | 'display_name'
  | 'role'
  | 'is_accounting'
  | 'primary_department_id'
  | 'department_ids'
  | 'manager_department_ids'
  | 'active'
> & { permissions?: Permissions; flow_role?: FlowRole };

/** 権限マトリクスの保存(権限管理画面から使う) */
export async function saveUserPermissions(
  uid: string,
  permissions: Permissions,
): Promise<void> {
  await updateDoc(doc(getDb(), 'users', uid), {
    permissions,
    updated_at: serverTimestamp(),
  });
}

/** users/{uid} を作成または更新する(uid は Firebase Auth のもの) */
/**
 * ユーザーの削除(2026-09-02 指示)。users ドキュメントを消す。
 * ログイン自体はできなくなる(この記録が無いとアプリに入れない)。
 * 過去の申請に残る氏名は引けなくなるため、退職者は「無効」を推奨する
 */
export async function deleteUserProfile(uid: string): Promise<void> {
  const users = (await fetchUsersFromServer()).filter((u) => u.id !== uid);
  const batch = writeBatch(getDb());
  batch.delete(doc(getDb(), 'users', uid));
  await addDepartmentManagerUpdates(batch, users);
  await batch.commit();
}

export async function saveUserProfile(uid: string, input: UserProfileInput): Promise<void> {
  const ref = doc(getDb(), 'users', uid);
  const all = await fetchUsersFromServer();
  const before = all.find((u) => u.id === uid);
  const after = { ...(before ?? {}), ...input, id: uid } as User;
  const batch = writeBatch(getDb());
  batch.set(
    ref,
    {
      ...input,
      ...(before ? {} : { created_at: serverTimestamp() }),
      updated_at: serverTimestamp(),
    },
    { merge: true },
  );
  // 役割・担当事業部・有効/無効が変わると事業部の部門長一覧も変わるため、同じ書き込みで更新する(HANDOFF #7)
  await addDepartmentManagerUpdates(batch, [...all.filter((u) => u.id !== uid), after]);
  await batch.commit();
}

/**
 * 事業部の部門長一覧(departments.manager_user_ids)を、名簿から計算し直して書き込みに加える(変わった事業部だけ)。
 * 権限ルールはこの一覧が空の事業部に限り、一般の申請者の部門長確認の省略を許す
 */
async function addDepartmentManagerUpdates(batch: WriteBatch, users: User[]): Promise<void> {
  const snap = await getDocsFromServer(collection(getDb(), 'departments'));
  const departments = snap.docs.map((d) => withId<Department>(d));
  const managers = departmentManagerIds(users, departments.map((d) => d.id));
  for (const d of departments) {
    const next = managers[d.id] ?? [];
    const current = [...(d.manager_user_ids ?? [])].sort();
    if (d.manager_user_ids && current.join(',') === next.join(',')) continue;
    batch.update(doc(getDb(), 'departments', d.id), { manager_user_ids: next, updated_at: serverTimestamp() });
  }
}

// --- 承認ルール -------------------------------------------------------------

export async function fetchApprovalRules(): Promise<ApprovalRule[]> {
  const snap = await getDocs(query(collection(getDb(), 'approval_rules'), orderBy('priority')));
  return snap.docs.map((d) => withId<ApprovalRule>(d));
}

export async function setApprovalRuleActive(id: string, active: boolean): Promise<void> {
  await updateDoc(doc(getDb(), 'approval_rules', id), { active });
}

// --- 設定 -------------------------------------------------------------------

export async function fetchFiscalSettings(): Promise<FiscalSettings> {
  const snap = await getDoc(doc(getDb(), 'settings', 'fiscal'));
  return (snap.data() as FiscalSettings | undefined) ?? { start_month: 4 };
}

export async function fetchTaxSettings(): Promise<TaxSettings> {
  const snap = await getDoc(doc(getDb(), 'settings', 'tax'));
  return (snap.data() as TaxSettings | undefined) ?? { mode: 'inclusive' };
}

export async function fetchPlVisibility(): Promise<PlVisibilitySettings> {
  const snap = await getDoc(doc(getDb(), 'settings', 'pl_visibility'));
  return (snap.data() as PlVisibilitySettings | undefined) ?? { company_pl_roles: ['admin'] };
}

export async function saveFiscalSettings(settings: FiscalSettings): Promise<void> {
  await setDoc(doc(getDb(), 'settings', 'fiscal'), settings, { merge: true });
}

/** 支払い者3択の表示名(v3.7)。選択肢自体は固定で、表示名のみ変更できる */
export async function fetchPayerLabels(): Promise<PayerLabels> {
  const snap = await getDoc(doc(getDb(), 'settings', 'payer_labels'));
  return { ...DEFAULT_PAYER_SELECT_LABELS, ...(snap.data() as Partial<PayerLabels> | undefined) };
}

export async function savePayerLabels(labels: PayerLabels): Promise<void> {
  await setDoc(doc(getDb(), 'settings', 'payer_labels'), labels, { merge: true });
}

/** 繰り返し支払いの設定(2026-09-07)。自動処理のON/OFF(既定OFF)と通知日数(既定: 支払日の1日前) */
export async function fetchRecurringSettings(): Promise<RecurringSettings> {
  const snap = await getDoc(doc(getDb(), 'settings', 'recurring'));
  return {
    auto_enabled: false,
    notice_days_before: 1,
    display_days_before: 7,
    ...(snap.data() as Partial<RecurringSettings> | undefined),
  };
}

export async function saveRecurringSettings(settings: RecurringSettings): Promise<void> {
  await setDoc(doc(getDb(), 'settings', 'recurring'), settings, { merge: true });
}
